import React, { useState, useRef, useEffect, useMemo } from 'react';
import CircularProgress from '@mui/material/CircularProgress';
import { useHotkeys } from 'react-hotkeys-hook';
import { FullScreen } from 'react-full-screen';
import ReactPlayer from 'react-player';
import { useClipFavourites, useSidebarPlayerSeek } from '../../ClipFavourites/context';
import { useClipAngleLock } from '../../ClipFavourites/useClipAngleLock';
import { useClipFilter } from '../../ClipFilter/context';
import { FavouriteFeedback } from '../../ClipFavourites/Star';
import { VideoDrawing } from '../../VideoDrawing';
import { useDrawingMarkers } from '../../VideoDrawing/context';
import cx from 'classnames';
import PlayCircleOutlineOutlinedIcon from '@mui/icons-material/PlayCircleOutlineOutlined';

import { CLIP, COMMENT, TOPIC, VIDEOFEED, VIDEO_TYPE } from '../../../enums/TYPES';
import { timeLineFormatTime } from '../../../helpers/timelineFormatTime';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import {
  getComposerDataByVideoFeedId,
  issueCloudFrontSignedCookies,
  shouldUseCredentialsForMediaUrl,
} from '../../../api';
import PlayIcon from '../../../assets/icons/play-icon.svg';
import {
  toFollowPlayhead,
  updateCurrentVideoThatInFrame,
  updateNotificationClick,
  updateSelectedVideoQuality,
  updateShowNotes,
} from '../../../store/sharedStore/sharedStoreSlice';
import { TRow } from '../../Timeline/TimelineRow';
import { getSelectedPlaybackClip } from '../../Timeline/utils';
import FullPageLoader from '../FullPageLoader';
import { MOBILE_SWITCH_ANGLE_EVENT } from '../MobileControlCenter';
import NotesForVideo from '../NotesForVideo';
import VideoControls from './VideoControls';
import css from './VideoPlayer.module.scss';
import {
  saveVideoLength,
  triggerRefresh,
  updateTimelineSeconds,
  updateVideoControls,
  updateVideoPlayedSeconds,
  saveClipToStore,
  updateNotes,
  updateQualifiers,
  playNextClipInOrder,
  playPreviousClipInOrder,
  saveSortedTimelineClips,
  updateSortedSelectedClips,
  imperativeMarkerSetter,
} from '../../../store/videos/videoSlice';
import {
  buildQueryString,
  findSearchValue,
  getComposerWebPreferences,
  rememberManualVolumeOverrideForFeed,
  getVideoQualityFromLocalStorage,
  isSafari,
  parseQueryString,
  setVideoQualityToLocalStorage,
  shouldApplyMutePreferenceForFeed,
  subscribeComposerWebPreferences,
} from '../../../helpers';
import { TClip } from '../../Timeline/TimelineClip';

type TVideoPlayer = {
  videoRows: TRow[];
  videoPlayerHeight: number;
  fullScreen: any;
  currentFeed: VIDEOFEED;
  ready: boolean;
};

const ignoreAngleShortcut = () =>
  Boolean(document.getElementById('clip-drawer-modal') || document.querySelector('[role="dialog"]'));

const VideoPlayer: React.FC<TVideoPlayer> = ({ videoRows, videoPlayerHeight, fullScreen, currentFeed, ready }) => {
  const PAUSE_OVERLAY_HIDE_EVENT = 'amplify:hide-pause-overlay';
  const PLAYED_SECONDS_DISPATCH_INTERVAL_MS = 250;

  const videoPlayerRef = useRef<ReactPlayer | null>(null);
  const controlRef = useRef<HTMLDivElement>(null);
  const [loading, setLoading] = useState(false);
  const {
    videoTimelineSeconds,
    videoControls,
    storeSelectedClip,
    videoFeed,
    videoLength,
    videoPlayedSeconds,
    refreshTrigger,
    markerPosition,
    sortedSelectedClips,
    playThisClip,
    notes,
    qualifiers,
  } = useAppSelector((state) => state.videoStore);
  const { selectedVideoQuality, notificationClick, showNotes } = useAppSelector((state) => state.sharedStore);
  const dispatch = useAppDispatch();
  const { editor, clipEntry, actions, playbackClips, playbackList } = useClipFavourites();
  const { active: clipFilterActive, dimmedIds } = useClipFilter();
  const { videoCommentSeek } = useDrawingMarkers();
  const videoControlsRef = useRef(videoControls);
  const safari = isSafari();
  const [clipIndex, setClipIndex] = useState(0);
  const [{ url: playbackUrl, videoId: playbackVideoId }, setPlaybackSource] = useState({ url: '', videoId: '' });
  const [playbackUrls, setPlaybackUrls] = useState<string[]>([]);
  const lastPlayedSecondsRef = useRef(0);
  const lastPlayedSecondsDispatchAtRef = useRef(0);
  const lastDispatchedPlayedSecondsRef = useRef(0);
  const pausedArrowPositionRef = useRef(videoPlayedSeconds);
  const pausedArrowRepeatRef = useRef<{ direction: 'left' | 'right' | null; count: number }>({
    direction: null,
    count: 0,
  });
  const safariPlayerRefs = useRef<(ReactPlayer | null)[]>([]);
  const videoDurationsRef = useRef<Record<string, number>>({});
  const lastMuteAllVideosRef = useRef(getComposerWebPreferences().muteAllVideos);
  const [playingArrowStepSeconds, setPlayingArrowStepSeconds] = useState(
    getComposerWebPreferences().playingArrowStepSeconds,
  );

  const [showVideoDrawingsWhenPlayingClips, setShowVideoDrawingsWhenPlayingClips] = useState(
    getComposerWebPreferences().showVideoDrawingsWhenPlayingClips,
  );

  const preferredQuality = selectedVideoQuality?.quality || getVideoQualityFromLocalStorage() || '';
  const resolveVideoUrl = (video?: VIDEO_TYPE | null) => {
    if (!video?.s3_keys) return '';
    if (preferredQuality && video.s3_keys[preferredQuality]) {
      return video.s3_keys[preferredQuality];
    }

    let highestKey = '';
    let highestValue = '';
    for (const key in video.s3_keys) {
      const value = video.s3_keys[key];
      if (value?.length && (!highestKey || parseInt(key) > parseInt(highestKey))) {
        highestKey = key;
        highestValue = value;
      }
    }

    return highestValue;
  };

  const resolvedSelectedVideoUrl = resolveVideoUrl(currentFeed?.videos?.[clipIndex]);
  const safariSourceUrls = useMemo(
    () => safari ? (currentFeed?.videos || []).map((video) => resolveVideoUrl(video)) : [],
    [safari, currentFeed?.videos, preferredQuality],
  );
  const playbackSourceUrls = useMemo(
    () => safari ? safariSourceUrls : resolvedSelectedVideoUrl ? [resolvedSelectedVideoUrl] : [],
    [safari, safariSourceUrls, resolvedSelectedVideoUrl],
  );

  const activePlaybackUrl = safari ? playbackUrls[clipIndex] || playbackUrls[0] || '' : playbackUrl;
  const feedId = findSearchValue('feedId');
  const playbackRateFeedId = useRef<string>();

  useEffect(() => {
    videoControlsRef.current = videoControls;
  }, [videoControls]);

  useEffect(() => {
    pausedArrowPositionRef.current = videoPlayedSeconds;
  }, [videoPlayedSeconds]);

  useEffect(() => {
    const applyMutePreference = (muteAllVideos: boolean) => {
      if (muteAllVideos) {
        if (!shouldApplyMutePreferenceForFeed(feedId)) return;
        dispatch(
          updateVideoControls({
            ...videoControlsRef.current,
            volume: 0,
          }),
        );
        lastMuteAllVideosRef.current = true;
        return;
      }

      if (lastMuteAllVideosRef.current && videoControlsRef.current.volume === 0) {
        dispatch(
          updateVideoControls({
            ...videoControlsRef.current,
            volume: 1,
          }),
        );
      }

      lastMuteAllVideosRef.current = false;
    };

    applyMutePreference(getComposerWebPreferences().muteAllVideos);
    return subscribeComposerWebPreferences((preferences) => {
      setPlayingArrowStepSeconds(preferences.playingArrowStepSeconds);
      setShowVideoDrawingsWhenPlayingClips(preferences.showVideoDrawingsWhenPlayingClips);
      applyMutePreference(preferences.muteAllVideos);
    });
  }, [dispatch, feedId]);

  useEffect(() => {
    if (!getComposerWebPreferences().muteAllVideos) return;
    if (!shouldApplyMutePreferenceForFeed(feedId)) return;
    if (videoControls.volume === 0) return;

    dispatch(
      updateVideoControls({
        ...videoControls,
        volume: 0,
      }),
    );
  }, [dispatch, feedId, videoControls]);

  const seekPlayersTo = (value: number, type: 'seconds' | 'fraction' = 'seconds') => {
    if (!Number.isFinite(value) || value < 0) return;

    if (safari) {
      safariPlayerRefs.current.forEach((player) => player?.seekTo(value, type));
      return;
    }

    videoPlayerRef.current?.seekTo(value, type);
  };
  const sidebarPlayer = useSidebarPlayerSeek(seekPlayersTo);

  useEffect(() => {
    let cancelled = false;

    const preparePlaybackUrls = async () => {
      const videoId = currentFeed?.videos?.[clipIndex]?.id || '';
      if (!playbackSourceUrls.some(Boolean)) {
        setPlaybackSource({ url: '', videoId: '' });
        setPlaybackUrls([]);
        return;
      }

      try {
        const responses = await Promise.all(
          playbackSourceUrls.map(async (url) => {
            if (!url) return '';

            try {
              const response = await issueCloudFrontSignedCookies(url);
              return response?.url || url;
            } catch {
              return url;
            }
          }),
        );

        if (!cancelled) {
          setPlaybackUrls(responses);
          setPlaybackSource({ url: responses[0] || '', videoId });
        }
      } catch {
        if (!cancelled) {
          setPlaybackUrls(playbackSourceUrls);
          setPlaybackSource({ url: playbackSourceUrls[0] || '', videoId });
        }
      }
    };

    preparePlaybackUrls();

    return () => {
      cancelled = true;
    };
  }, [playbackSourceUrls]);

  useEffect(() => {
    if (safari) {
      videoPlayerRef.current = safariPlayerRefs.current[clipIndex] || null;
    }
  }, [safari, clipIndex, playbackUrls]);

  const handlePlaybackError = () => {
    console.error('Media playback failed', resolvedSelectedVideoUrl);
  };

  const switchFullScreen = () => {
    if (!fullScreen.active) {
      fullScreen.enter();
    } else {
      fullScreen.exit();
    }
  };

  useEffect(() => {
    window.addEventListener('keydown', (e) => {
      if (e.keyCode === 9) {
        // keyCode 9 represents the Tab key
        e.preventDefault();
      }
    });
  }, []);
  useHotkeys('control + f, shift + f, f', () => switchFullScreen());
  useHotkeys('Esc', () => fullScreen.exit());
  useHotkeys('Space', () => {
    if (ready) {
      playPauseHandler();
    }
  });
  useHotkeys('m', () =>
    {
      const nextVolume = videoControls.volume === 0 ? 1 : 0;
      rememberManualVolumeOverrideForFeed(feedId, nextVolume);
      dispatch(
        updateVideoControls({
          ...videoControls,
          volume: nextVolume,
        }),
      );
    },
  );
  const fps = storeSelectedClip?.fps || currentFeed?.videos?.[0]?.fps || 25;
  const frameStep = 1 / fps;
  const getPausedArrowStep = (direction: 'left' | 'right', isRepeat: boolean) => {
    if (!isRepeat || pausedArrowRepeatRef.current.direction !== direction) {
      pausedArrowRepeatRef.current = { direction, count: 0 };
      return frameStep;
    }

    const count = pausedArrowRepeatRef.current.count + 1;
    pausedArrowRepeatRef.current.count = count;
    return frameStep * (count >= 18 ? 10 : count >= 6 ? 5 : 1);
  };

  useHotkeys('right', (event) => {
    if ((event.target as HTMLElement | null)?.closest('[data-clip-filter-panel]')) return;
    if (!videoControls.playing) {
      const step = getPausedArrowStep('right', event.repeat);
      const maxSeconds = videoLength > 0 ? videoLength / 1000 : Number.POSITIVE_INFINITY;
      const next = Math.min(maxSeconds, pausedArrowPositionRef.current + step);
      pausedArrowPositionRef.current = next;
      seekPlayersTo(next);
      dispatch(updateVideoPlayedSeconds(next));
      return;
    }

    if (!sidebarPlayer.active && sortedSelectedClips.length > 1) {
      const clip = sortedSelectedClips.find(
        (iteratedClip) => iteratedClip.rowIndex === markerPosition.rowIndex && iteratedClip.clipIndex === markerPosition.clipIndex,
      );
      if (clipFilterActive && !clip) { actions.current?.pause(); return; }
      if (videoPlayerRef.current!.getCurrentTime() + playingArrowStepSeconds > clip!.time_end!) {
        if (clipFilterActive && clip) {
          sidebarPlayer.onEnded(clip.time_end);
          return;
        }
        // @ts-ignore
        dispatch(playNextClipInOrder());
      } else {
        seekPlayersTo(videoPlayedSeconds + playingArrowStepSeconds);
      }
    } else if (sortedSelectedClips.length === 1) {
      seekPlayersTo(videoPlayedSeconds + playingArrowStepSeconds);
    } else {
      seekPlayersTo(videoPlayedSeconds + playingArrowStepSeconds);
    }
  });
  useHotkeys('left', (event) => {
    if ((event.target as HTMLElement | null)?.closest('[data-clip-filter-panel]')) return;
    if (!videoControls.playing) {
      const step = getPausedArrowStep('left', event.repeat);
      const prev = Math.max(0, pausedArrowPositionRef.current - step);
      pausedArrowPositionRef.current = prev;
      seekPlayersTo(prev);
      dispatch(updateVideoPlayedSeconds(prev));
      return;
    }

    if (!sidebarPlayer.active && sortedSelectedClips.length > 1) {
      const clip = sortedSelectedClips.find(
        (iteratedClip) => iteratedClip.rowIndex === markerPosition.rowIndex && iteratedClip.clipIndex === markerPosition.clipIndex,
      );
      if (clipFilterActive && !clip) { actions.current?.pause(); return; }
      seekPlayersTo(videoPlayedSeconds - playingArrowStepSeconds);
      if (videoPlayerRef.current!.getCurrentTime() - playingArrowStepSeconds < clip!.time_start!) {
        if (clipFilterActive && clip) {
          const currentIndex = sortedSelectedClips.findIndex((item) => String(item.id) === String(clip.id));
          const previous = sortedSelectedClips.slice(0, currentIndex).reverse().find((item) => !dimmedIds.has(String(item.id))) ||
            sortedSelectedClips.find((item) => !dimmedIds.has(String(item.id)));
          if (previous) {
            actions.current?.seek(previous, true);
            seekPlayersTo(previous.time_start);
          } else actions.current?.pause();
          return;
        }
        // @ts-ignore
        dispatch(playPreviousClipInOrder());
      }
    } else if (sortedSelectedClips.length === 1) {
      seekPlayersTo(videoPlayedSeconds - playingArrowStepSeconds);
    } else if (videoPlayerRef.current!.getCurrentTime() - playingArrowStepSeconds < 0) {
      seekPlayersTo(0);
      dispatch(updateVideoPlayedSeconds(0));
    } else {
      seekPlayersTo(videoPlayedSeconds - playingArrowStepSeconds);
    }
  });

  const [videoData, setVideoData] = useState<VIDEO_TYPE | null>(null);

  const handleDuration = (duration: number, videoId?: string) => {
    const durationInMilliseconds = duration * 1000;
    if (videoId) {
      videoDurationsRef.current[videoId] = durationInMilliseconds;
    }
    if (!videoId || videoId === currentFeed?.videos?.[clipIndex]?.id) {
      dispatch(saveVideoLength(durationInMilliseconds));
    }
  };

  useEffect(() => {
    const activeVideoId = currentFeed?.videos?.[clipIndex]?.id;
    const duration = activeVideoId && videoDurationsRef.current[activeVideoId];
    if (duration) {
      dispatch(saveVideoLength(duration));
    }
  }, [clipIndex, currentFeed?.videos, dispatch]);

  useEffect(() => {
    if (!currentFeed?.videos?.length) {
      return;
    }

    const selectedClipFromFeed = storeSelectedClip
      ? currentFeed.videos.find((video) => video.id === storeSelectedClip.id)
      : null;
    const nextVideo = selectedClipFromFeed || currentFeed.videos[0];

    if (nextVideo && playbackRateFeedId.current !== String(currentFeed.id)) {
      playbackRateFeedId.current = String(currentFeed.id);
      dispatch(updateVideoControls({ ...videoControls, playbackRate: 1.0 }));
    }

    if (nextVideo && nextVideo.id !== videoData?.id) {
      const duration = videoDurationsRef.current[nextVideo.id] || Number(nextVideo.duration) * 1000;
      if (duration > 0) {
        dispatch(saveVideoLength(duration));
      }
      setVideoData(nextVideo);
    }

    if (!selectedClipFromFeed && nextVideo && nextVideo.id !== storeSelectedClip?.id) {
      dispatch(saveClipToStore(nextVideo));
    }
  }, [storeSelectedClip, refreshTrigger, currentFeed]);

  const changePlayBackRate = (rate: number) => {
    dispatch(updateVideoControls({ ...videoControls, playbackRate: rate }));
  };

  const restorePlaybackRate = () => {
    const video = videoPlayerRef.current?.getInternalPlayer();
    if (video instanceof HTMLVideoElement) video.playbackRate = videoControlsRef.current.playbackRate;
  };

  const playPauseHandler = () => {
    dispatch(toFollowPlayhead(!videoControls.playing));
    if (!videoControls.playing && !sidebarPlayer.active && !clipFilterActive) {
      const marked = editor.rows[markerPosition.rowIndex]?.clips[markerPosition.clipIndex];
      const clip = getSelectedPlaybackClip(sortedSelectedClips, videoPlayedSeconds, marked?.id);
      if (clip && actions.current) {
        actions.current.seek(clip, true, videoPlayedSeconds >= clip.time_start && videoPlayedSeconds < clip.time_end
          ? videoPlayedSeconds : clip.time_start);
        return;
      }
    }
    if (videoPlayedSeconds < videoTimelineSeconds) {
      dispatch(updateVideoPlayedSeconds(videoControls.playedSeconds));
      dispatch(updateTimelineSeconds(videoControls.playedSeconds));
      dispatch(
        updateVideoControls({
          ...videoControls,
          playedSeconds: videoControls.playedSeconds,
          playing: !videoControls.playing,
        }),
      );
    } else {
      dispatch(
        updateVideoControls({
          ...videoControls,
          playing: !videoControls.playing,
        }),
      );
    }
  };

  const refreshSeekRef = useRef({ videoId: storeSelectedClip?.id, seconds: 0 });
  useEffect(() => {
    const previous = refreshSeekRef.current;
    refreshSeekRef.current = { videoId: storeSelectedClip?.id, seconds: videoPlayedSeconds };
    // Angle changes restore their position separately; refresh is also used for timeline seeks.
    if (
      previous.videoId === storeSelectedClip?.id &&
      videoPlayedSeconds > 0 &&
      previous.seconds.toFixed(1) !== videoPlayedSeconds.toFixed(1)
    ) {
      seekPlayersTo(videoPlayedSeconds);
    }
  }, [storeSelectedClip?.id, refreshTrigger]);

  useEffect(() => {
    seekPlayersTo(videoTimelineSeconds);
  }, [videoTimelineSeconds]);

  const jumpToTaggedTime = (time: number, shouldAutoPlay = false) => {
    dispatch(toFollowPlayhead(true));
    const feedId = findSearchValue('feedId');
    const obj = parseQueryString(window.location.search);
    const selected = currentFeed;
    if (selected) {
      dispatch(saveClipToStore(selected.videos[0]));
    }
    seekPlayersTo(time);
    dispatch(updateVideoPlayedSeconds(time));
    if (shouldAutoPlay) {
      dispatch(updateVideoControls({ ...videoControls, playing: true, playedSeconds: time }));
    }
    delete obj.timeToJump;
    delete obj.autoplay;
    const query = buildQueryString(obj);
    window.history.replaceState(null, '', `?${query}`);
    setLoading(false);
    dispatch(triggerRefresh());
  };

  const searchParamsUrl = window.location.search;
  const [feedData, setFeedData] = useState<VIDEOFEED>();
  const autoPlayRef = useRef(false);
  const getVideofeedData = async (time: number, shouldAutoPlay = false) => {
    setLoading(true);
    try {
      if (feedId) {
        const res = await getComposerDataByVideoFeedId(feedId);
        setFeedData(res.data);
        if (res.data.topics.length === 0) {
          jumpToTaggedTime(time!, shouldAutoPlay);
        }
      }
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    let timeToJump = findSearchValue('timeToJump');
    const shouldAutoPlay = !!findSearchValue('autoplay');
    autoPlayRef.current = shouldAutoPlay;
    if (timeToJump && timeToJump.includes('%')) {
      timeToJump = timeToJump.split('%')[0];
    }
    if (timeToJump && !isNaN(+timeToJump) && videoRows?.length && videoLength) {
      setLoading(true);
      setTimeout(() => {
        jumpToTaggedTime(+timeToJump!, shouldAutoPlay);
      }, 2000);
    } else if (feedId && videoLength && timeToJump && !isNaN(+timeToJump)) {
      getVideofeedData(+timeToJump, shouldAutoPlay);
    } else if (shouldAutoPlay && videoLength && !findSearchValue('clipToJump')) {
      dispatch(updateVideoControls({ ...videoControls, playing: true }));
      const obj = parseQueryString(window.location.search);
      delete obj.autoplay;
      const query = buildQueryString(obj);
      window.history.replaceState(null, '', `?${query}`);
    }
  }, [searchParamsUrl, videoRows, videoLength]);

  const progressHandler = ({
    loaded,
    loadedSeconds,
    played,
    playedSeconds,
  }: {
    loaded: number;
    loadedSeconds: number;
    played: number;
    playedSeconds: number;
  }) => {
    if (!safari && pendingClipSwitchRef.current) return;
    if (videoControls.playing && !videoControlsRef.current.playing) return;

    const maybeDispatchPlayedSeconds = (seconds: number, force = false) => {
      if (!Number.isFinite(seconds) || seconds <= 0) return;
      const now = Date.now();
      const elapsed = now - lastPlayedSecondsDispatchAtRef.current;
      const changed = Math.abs(seconds - lastDispatchedPlayedSecondsRef.current) >= 0.001;
      if (force || (elapsed >= PLAYED_SECONDS_DISPATCH_INTERVAL_MS && changed)) {
        lastPlayedSecondsDispatchAtRef.current = now;
        lastDispatchedPlayedSecondsRef.current = seconds;
        dispatch(updateVideoPlayedSeconds(seconds));
      }
    };

    if (Number.isFinite(playedSeconds) && playedSeconds > 0) {
      lastPlayedSecondsRef.current = playedSeconds;
    }

    if (playedSeconds >= videoLength) {
      maybeDispatchPlayedSeconds(playedSeconds, true);
      dispatch(
        updateVideoControls({
          ...videoControlsRef.current,
          loaded,
          loadedSeconds,
          played,
          playedSeconds,
          playing: false,
        }),
      );
    } else {
      maybeDispatchPlayedSeconds(playedSeconds);
      dispatch(
        updateVideoControls({
          ...videoControlsRef.current,
          loaded,
          loadedSeconds,
          played,
          playedSeconds,
        }),
      );
    }
  };

  const seekHandler = (e: Event | React.SyntheticEvent<Element, Event>, value: number | number[]) => {
    dispatch(updateVideoControls({ ...videoControls, played: Number(value) / 100 }));
    seekPlayersTo(Number(value) / 100, 'fraction');
  };

  const seekMouseUpHandler = (e: Event | React.SyntheticEvent<Element, Event>, value: number | number[]) => {
    dispatch(updateVideoControls({ ...videoControls, seeking: false }));
    seekPlayersTo(Number(value) / 100, 'fraction');
  };

  const onSeekMouseDownHandler = () => {
    dispatch(updateVideoControls({ ...videoControls, seeking: true }));
  };

  const currentTime = videoPlayerRef.current ? videoPlayerRef.current.getCurrentTime() : '00:00';

  const formatCurrentTime = timeLineFormatTime(currentTime);

  const bufferStartHandler = () => {
    dispatch(updateVideoControls({ ...videoControls, buffer: true }));
  };

  const bufferEndHandler = () => {
    dispatch(updateVideoControls({ ...videoControls, buffer: false }));
  };

  const videoVolumeChange = (volume: number) => {
    rememberManualVolumeOverrideForFeed(feedId, volume);
    dispatch(updateVideoControls({ ...videoControls, volume }));
  };

  const urlParams = document.location.search;
  const clipRegex = findSearchValue('clip');
  const [feedIndex, setFeedIndexx] = useState(0);

  useEffect(() => {
    if (clipRegex) {
      setClipIndex(+clipRegex);
    }
  }, [clipRegex]);

  useEffect(() => {
    const lastIndex = (currentFeed?.videos?.length || 0) - 1;
    if (lastIndex < 0) return;
    if (clipIndex < 0 || clipIndex > lastIndex) {
      setClipIndex(0);
    }
  }, [clipIndex, currentFeed?.videos?.length]);

  const pendingClipSwitchRef = useRef<{ time: number; wasPlaying: boolean; videoId: string } | null>(null);
  const lastFeedIdRef = useRef<string | null>(feedId || null);

  const applyClipSwitchSeek = () => {
    if (findSearchValue('timeToJump') || findSearchValue('clipToJump')) {
      pendingClipSwitchRef.current = null;
      return;
    }

    const pending = pendingClipSwitchRef.current;
    if (!pending) return;

    if (safari) {
      const nextIndex = currentFeed.videos.findIndex((video) => video.id === pending.videoId);
      const nextPlayer = safariPlayerRefs.current[nextIndex];
      const angleTime = nextPlayer?.getCurrentTime();
      if (!nextPlayer || angleTime == null) return;

      const tolerance = pending.wasPlaying ? 1 / (currentFeed.videos[nextIndex]?.fps || 25) : 0.001;
      pendingClipSwitchRef.current = null;
      // Background angles already play in sync; only correct the newly selected angle if needed.
      if (Math.abs(angleTime - pending.time) > tolerance) {
        nextPlayer.seekTo(pending.time, 'seconds');
      }
      return;
    }

    // The previous angle can finish buffering while the requested angle is still loading.
    if (!videoPlayerRef.current || playbackVideoId !== pending.videoId) return;

    seekPlayersTo(pending.time);
    dispatch(updateVideoPlayedSeconds(pending.time));
    dispatch(updateVideoControls({ ...videoControls, playedSeconds: pending.time, playing: pending.wasPlaying }));
    pendingClipSwitchRef.current = null;
  };

  const switchVideoClip = (currentClipIndex: number, targetClipIndex?: number, targetTime?: number) => {
    const nextIndex =
      targetClipIndex ?? (currentClipIndex === currentFeed.videos.length - 1 ? 0 : currentClipIndex + 1);
    const nextVideo = currentFeed.videos[nextIndex];
    if (!nextVideo || nextIndex === currentClipIndex) return;

    const currentTime = videoPlayerRef.current?.getCurrentTime?.() ?? 0;
    const keepTime = targetTime ?? (
      (Number.isFinite(currentTime) && currentTime > 0 ? currentTime : 0) ||
      lastPlayedSecondsRef.current ||
      videoPlayedSeconds ||
      videoControls.playedSeconds ||
      0);
    if (keepTime > 0) {
      lastPlayedSecondsRef.current = keepTime;
      dispatch(updateVideoPlayedSeconds(keepTime));
      dispatch(updateVideoControls({ ...videoControls, playedSeconds: keepTime }));
    }
    pendingClipSwitchRef.current = { time: keepTime, wasPlaying: videoControls.playing, videoId: nextVideo.id };

    dispatch(saveClipToStore(nextVideo));
    const paramsObj = parseQueryString(window.location.search);
    paramsObj.clip = `${nextIndex}`;
    paramsObj.clipId = nextVideo.id;
    window.history.replaceState(null, '', `?${buildQueryString(paramsObj)}`);
    setClipIndex(nextIndex);
    dispatch(triggerRefresh());
  };

  useHotkeys(
    ['1', '2', '3', '4', 'shift+1', 'shift+2', 'shift+3', 'shift+4'],
    (event) => {
      if (event.repeat) return;
      const angleKey = /^(?:Digit|Numpad)([1-4])$/.exec(event.code)?.[1] ?? event.key;
      switchVideoClip(clipIndex, Number(angleKey) - 1);
    },
    { ignoreEventWhen: ignoreAngleShortcut },
  );

  useHotkeys(
    'c',
    (event) => {
      if (event.repeat) return;
      switchVideoClip(clipIndex);
    },
    { ignoreEventWhen: ignoreAngleShortcut },
  );

  useEffect(() => {
    const handleMobileSwitchAngle = () => {
      if (window.innerWidth < 768) switchVideoClip(clipIndex);
    };
    window.addEventListener(MOBILE_SWITCH_ANGLE_EVENT, handleMobileSwitchAngle);
    return () => window.removeEventListener(MOBILE_SWITCH_ANGLE_EVENT, handleMobileSwitchAngle);
  }, [clipIndex, currentFeed, safari, videoControls, videoPlayedSeconds]);

  // On clip/angle change keep playback time using last stable second.
  useEffect(() => {
    if (!storeSelectedClip) return;
    if (findSearchValue('timeToJump') || findSearchValue('clipToJump')) {
      pendingClipSwitchRef.current = null;
      return;
    }
    const currentFeedId = feedId || null;
    if (lastFeedIdRef.current !== currentFeedId) {
      lastFeedIdRef.current = currentFeedId;
      lastPlayedSecondsRef.current = 0;
      pendingClipSwitchRef.current = null;
      return;
    }

    if (pendingClipSwitchRef.current?.videoId !== storeSelectedClip.id) {
      const keepTime =
        (!safari && videoPlayerRef.current?.getCurrentTime?.()) ||
        lastPlayedSecondsRef.current || videoPlayedSeconds || videoControls.playedSeconds || 0;
      pendingClipSwitchRef.current = { time: keepTime, wasPlaying: videoControls.playing, videoId: storeSelectedClip.id };
    }
    if (safari) applyClipSwitchSeek();
  }, [storeSelectedClip?.id, feedId]);

  const angleLockSeconds = videoControls.playing ? videoControls.playedSeconds : videoPlayedSeconds;
  const markedClip = editor.rows[markerPosition.rowIndex]?.clips[markerPosition.clipIndex];
  const insideMarkedClip = !!markedClip && angleLockSeconds >= markedClip.time_start - 0.002 &&
    angleLockSeconds <= markedClip.time_end;
  const selectedPlaybackClip = String(currentFeed?.id) === feedId
    ? sortedSelectedClips.find((clip) => String(clip.id) === String(markedClip?.id)) : undefined;
  const selectedDrawingClip = String(currentFeed?.id) === feedId
    ? (insideMarkedClip ? selectedPlaybackClip : undefined) || sortedSelectedClips.find((clip) =>
      angleLockSeconds >= clip.time_start && angleLockSeconds <= clip.time_end) : undefined;
  const drawingPlaybackClip = selectedDrawingClip || selectedPlaybackClip || (String(currentFeed?.id) === feedId && playbackList === 'favourites'
    ? playbackClips?.find((clip) => String(clip.id) === String(markedClip?.id)) : undefined);
  const drawingAngleOverrideClip = useRef<string | null>(null);
  const angleLockReady = insideMarkedClip && String(currentFeed?.id) === feedId && !!currentFeed?.videos?.[clipIndex] &&
    clipIndex === Number(clipRegex || 0);
  useEffect(() => {
    const pending = pendingClipSwitchRef.current;
    // A second clip entry while an angle loads must replace the earlier seek target.
    if (pending && insideMarkedClip) {
      pending.time = angleLockSeconds;
      pending.wasPlaying = videoControls.playing;
    }
  }, [markedClip?.id, clipEntry, videoControls.playing]);
  useClipAngleLock({
    feedId,
    clip: markedClip,
    videos: currentFeed?.videos || [],
    angleIndex: clipIndex,
    ready: angleLockReady,
    switchAngle: (index) => {
      if (drawingAngleOverrideClip.current !== String(markedClip?.id)) switchVideoClip(clipIndex, index, angleLockSeconds);
    },
  });
  useEffect(() => {
    if (angleLockReady) drawingAngleOverrideClip.current = null;
  }, [angleLockReady, markedClip?.id, clipEntry]);

  useEffect(() => {
    // if feed index changes, we reset the all controls
    dispatch(updateVideoPlayedSeconds(0));
    dispatch(updateTimelineSeconds(0));
    dispatch(updateVideoControls({ ...videoControlsRef.current, playedSeconds: 0, played: 0, playing: false }));
    seekPlayersTo(0);
    dispatch(updateSortedSelectedClips([]));
    dispatch(updateNotes([]));
    dispatch(updateQualifiers([]));
    if (currentFeed) {
      const clipsArray: CLIP[] = currentFeed.topics.reduce((acc: CLIP[], topic: TOPIC) => {
        return acc.concat(topic.clips);
      }, []);
      clipsArray.sort((a, b) => a.time_start - b.time_start);
      dispatch(saveSortedTimelineClips(clipsArray));
    }
  }, [feedIndex]);

  useEffect(() => {
    if (feedId) {
      // retrieve feed index from url params
      handleFeedIndex(feedId);
      // retrieve clip index from url params
      const clipInd = findSearchValue('clip');
      if (clipInd) {
        setClipIndex(+clipInd);
      }
    }
  }, [urlParams, feedId, currentFeed]);

  const handleFeedIndex = (id: string) => {
    try {
      const indexOfFeed = videoFeed.findIndex((f) => f.id === id);
      if (indexOfFeed !== -1) {
        setFeedIndexx(indexOfFeed);
      }
    } catch (err) {
      console.log(err);
    }
  };

  const [showControls, setShowControls] = useState(false);

  const displayControls = () => {
    setShowControls(true);
  };

  const hideControls = () => {
    setShowControls(false);
  };

  const onVideoQualitySet = (videoQualityArr: [string, string]) => {
    // videoQualityArr: ['1080', 'www.video.com/video.mp4']
    dispatch(updateSelectedVideoQuality({ quality: videoQualityArr[0], url: videoQualityArr[1] }));
    setVideoQualityToLocalStorage(`${videoQualityArr[0]}`);
  };
  useEffect(() => {
    if (videoData) {
      dispatch(updateCurrentVideoThatInFrame(videoData));
    }
    const rememberedQuality = getVideoQualityFromLocalStorage(); // string like "1080"
    if (videoData) {
      if (rememberedQuality?.length && videoData.s3_keys[rememberedQuality] && videoData.s3_keys[rememberedQuality]?.length > 0) {
        onVideoQualitySet([rememberedQuality, videoData.s3_keys[rememberedQuality]]); //['1080', 'www.video.com/video.mp4']
      } else {
        let highestKey = null;
        let highestValue = null;
        for (const key in videoData?.s3_keys) {
          if ((!highestKey || parseInt(key) > parseInt(highestKey)) && videoData.s3_keys[key].length > 0) {
            highestKey = key;
            highestValue = videoData.s3_keys[key];
          }
        }
        if (highestKey && highestValue) {
          onVideoQualitySet([highestKey, highestValue]);
        }
      }
    }
  }, [videoData]);

  const disableBuffering = () => {
    dispatch(updateVideoControls({ ...videoControls, buffer: false }));
  };

  useEffect(() => {
    seekPlayersTo(videoPlayedSeconds);
  }, [selectedVideoQuality?.quality]);

  const switchNotesCallBack = () => {
    dispatch(updateShowNotes(!showNotes));
  };

  const heightOfPlayer = useMemo(() => {
    if (showNotes && (notes?.length || qualifiers?.length)) {
      return '100%';
    } else {
      return '100%';
    }
  }, [videoPlayerHeight, showNotes, notes, qualifiers]);

  const [jumped, setJumped] = useState(false);

  const jumpToClip = (clipId: number) => {
    let selectedClip: TClip | undefined;
    const shouldAutoPlay = autoPlayRef.current || !!findSearchValue('autoplay');

    if (clipId && videoRows.length && (!jumped || notificationClick)) {
      videoRows.forEach((row, rowIndex) => {
        row.clips.forEach((clip, i) => {
          if (+clip.id === +clipId) {
            selectedClip = clip;
            selectedClip.rowIndex = rowIndex;
            selectedClip.clipIndex = i;
          }
        });
      });

      if (selectedClip) {
        dispatch(
          updateVideoControls({
            ...videoControls,
            playing: shouldAutoPlay,
            playedSeconds: selectedClip.time_start,
          }),
        );

        dispatch(updateVideoPlayedSeconds(selectedClip.time_start));

        dispatch(
          updateSortedSelectedClips([
            {
              ...selectedClip,
              rowIndex: selectedClip.rowIndex!,
              clipIndex: selectedClip.clipIndex!,
              topicId: videoRows[selectedClip.rowIndex!].id,
              topicName: videoRows[selectedClip.rowIndex!].name,
            },
          ]),
        );

        dispatch(toFollowPlayhead(true));
        dispatch(updateNotificationClick(false));
        setJumped(true);
        setTimeout(() => {
          dispatch(
            imperativeMarkerSetter({
              rowIndex: selectedClip!.rowIndex!,
              clipIndex: selectedClip!.clipIndex!,
              seconds: selectedClip!.time_start,
            }),
          );
          seekPlayersTo(selectedClip!.time_start);
          if (shouldAutoPlay) {
            dispatch(updateVideoControls({ ...videoControls, playing: true, playedSeconds: selectedClip!.time_start }));
          }
          const obj = parseQueryString(window.location.search);
          delete obj.autoplay;
          const query = buildQueryString(obj);
          window.history.replaceState(null, '', `?${query}`);
          dispatch(triggerRefresh());
        });
      }
    }
  };

  const clipToJumpQuery = findSearchValue('clipToJump');
  // Main useEffect to trigger the jump
  useEffect(() => {
    if (videoRows?.length && clipToJumpQuery?.length && clipToJumpQuery !== 'undefined' && videoLength > 0) {
      const clipToJump = findSearchValue('clipToJump');
      jumpToClip(+clipToJump!);
    }
    return () => setJumped(false);
  }, [videoRows, clipToJumpQuery, videoLength, notificationClick, feedId]);

  const [showPauseIcon, setShowPauseIcon] = useState(false);
  const [hidePauseUntilMouseMove, setHidePauseUntilMouseMove] = useState(false);
  const timeoutRef = useRef<any>(null);

  useEffect(() => {
    if (videoControls.playing) {
      setHidePauseUntilMouseMove(false);
      setShowPauseIcon(false);
      clearTimeout(timeoutRef.current);
      return;
    }

    // Show icon when paused unless hidden by frame-step.
    if (!hidePauseUntilMouseMove) {
      setShowPauseIcon(true);
      startHideTimer();
    }
  }, [videoControls.playing, hidePauseUntilMouseMove]);

  useEffect(() => {
    const handleHidePauseOverlay = (event: Event) => {
      const detail = (event as CustomEvent<{ context?: string }>).detail;
      if (detail?.context !== 'timeline') return;
      setHidePauseUntilMouseMove(true);
      setShowPauseIcon(false);
      clearTimeout(timeoutRef.current);
    };

    window.addEventListener(PAUSE_OVERLAY_HIDE_EVENT, handleHidePauseOverlay as EventListener);
    return () => {
      window.removeEventListener(PAUSE_OVERLAY_HIDE_EVENT, handleHidePauseOverlay as EventListener);
    };
  }, []);

  useEffect(
    () => () => {
      clearTimeout(timeoutRef.current);
    },
    [],
  );

  const startHideTimer = () => {
    clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => setShowPauseIcon(false), 5000);
  };

  const handleMouseMove = () => {
    if (!videoControls.playing) {
      if (hidePauseUntilMouseMove) {
        setHidePauseUntilMouseMove(false);
      }
      setShowPauseIcon(true);
      startHideTimer();
    }
  };

  useEffect(() => {
    if (!videoCommentSeek || videoCommentSeek.feedId !== feedId) return;
    const { seconds } = videoCommentSeek;
    // Each click is a seek, even when its timestamp matches the previous request.
    if (pendingClipSwitchRef.current) {
      pendingClipSwitchRef.current.time = seconds;
      pendingClipSwitchRef.current.wasPlaying = false;
    }
    videoControlsRef.current = { ...videoControlsRef.current, playedSeconds: seconds, playing: false };
    lastPlayedSecondsRef.current = seconds;
    videoPlayerRef.current?.getInternalPlayer()?.pause?.();
    seekPlayersTo(seconds);
  }, [videoCommentSeek, feedId]);

  return (
    <>
      {activePlaybackUrl && (
        <div
          className={css.wrapper}
          style={{ height: heightOfPlayer }}
          onMouseOver={displayControls}
          onMouseLeave={hideControls}
          onFocus={displayControls}
          onMouseMove={handleMouseMove}
        >
          {videoData && videoData?.s3_keys && activePlaybackUrl && (
            <FullScreen handle={fullScreen} className={css.fullScreenWrapper}>
              <div className={css.videoStage} data-drawing-stage>
              <FavouriteFeedback video />
              {safari ? (
                playbackUrls.map(
                  (url, index) =>
                    !!url && (
                      <ReactPlayer
                        key={`${currentFeed?.id || 'feed'}-${index}-${url}`}
                        className={css.player}
                        width="100%"
                        height="100%"
                        ref={(player) => {
                          safariPlayerRefs.current[index] = player;
                          if (index === clipIndex) {
                            videoPlayerRef.current = player;
                          }
                        }}
                        id={index === clipIndex ? 'videoPlayerRef' : undefined}
                        progressInterval={100}
                        onProgress={index === clipIndex ? progressHandler : undefined}
                        onEnded={index === clipIndex ? () => sidebarPlayer.onEnded(videoPlayerRef.current?.getCurrentTime() ?? 0) : undefined}
                        playing={videoControls.playing}
                        playbackRate={videoControls.playbackRate}
                        volume={index === clipIndex ? videoControls.volume : 0}
                        muted={index !== clipIndex || videoControls.volume === 0}
                        onBuffer={index === clipIndex ? bufferStartHandler : undefined}
                        onReady={
                          index === clipIndex
                            ? () => {
                                restorePlaybackRate();
                                disableBuffering();
                                applyClipSwitchSeek();
                              }
                            : undefined
                        }
                        onError={index === clipIndex ? handlePlaybackError : undefined}
                        onBufferEnd={
                          index === clipIndex
                            ? () => {
                                bufferEndHandler();
                                applyClipSwitchSeek();
                              }
                            : undefined
                        }
                        url={url}
                        onDuration={(duration) => handleDuration(duration, currentFeed?.videos?.[index]?.id)}
                        responsetype="stream"
                        pip={false}
                        playsinline
                        style={{
                          opacity: index === clipIndex ? 1 : 0,
                          pointerEvents: index === clipIndex ? 'auto' : 'none',
                          transition: 'opacity 120ms linear',
                          zIndex: index === clipIndex ? 1 : 0,
                        }}
                        config={{
                          file: {
                            hlsOptions: {
                              xhrSetup: (xhr: XMLHttpRequest) => {
                                xhr.withCredentials = shouldUseCredentialsForMediaUrl(url);
                              },
                            },
                            attributes: {
                              playsInline: true,
                              preload: 'auto',
                              crossOrigin: shouldUseCredentialsForMediaUrl(url)
                                ? 'use-credentials'
                                : 'anonymous',
                            },
                          },
                        }}
                      />
                    ),
                )
              ) : (
                <ReactPlayer
                  className={css.player}
                  width="100%"
                  height="100%"
                  ref={videoPlayerRef}
                  id={'videoPlayerRef'}
                  progressInterval={100}
                  onProgress={progressHandler}
                  onEnded={() => sidebarPlayer.onEnded(videoPlayerRef.current?.getCurrentTime() ?? 0)}
                  playing={videoControls.playing}
                  playbackRate={videoControls.playbackRate}
                  volume={videoControls.volume}
                  onBuffer={bufferStartHandler}
                  onReady={() => {
                    restorePlaybackRate();
                    disableBuffering();
                    applyClipSwitchSeek();
                  }}
                  onError={handlePlaybackError}
                  onBufferEnd={() => {
                    bufferEndHandler();
                    applyClipSwitchSeek();
                  }}
                  url={playbackUrl}
                  onDuration={(duration) => handleDuration(duration, videoData?.id)}
                  responsetype="stream"
                  pip={false}
                  playsinline
                  config={{
                    file: {
                      hlsOptions: {
                        xhrSetup: (xhr: XMLHttpRequest) => {
                          xhr.withCredentials = shouldUseCredentialsForMediaUrl(playbackUrl);
                        },
                      },
                      attributes: {
                        playsInline: true,
                        preload: 'auto',
                        crossOrigin: shouldUseCredentialsForMediaUrl(playbackUrl)
                          ? 'use-credentials'
                          : 'anonymous',
                      },
                    },
                  }}
                />
              )}

              {/* {videoControls.buffer && (
                <div className={css.videoLinearLoader}>
                  <CircularProgress color="inherit" />
                </div>
              )} */}

              <div
                className={css.videoAngleBtn}
                onClick={() => switchVideoClip(clipIndex)}
                style={{ display: videoPlayerHeight > 60 ? 'flex' : 'none' }}
              >
                <div className={css.index}>{clipIndex + 1}</div>
              </div>

              <VideoDrawing playing={videoControls.playing} playerRef={videoPlayerRef}
                sourceKey={`${currentFeed?.id}:${currentFeed?.videos?.[clipIndex]?.id}`}
                feedId={feedId || ''} videoId={String(currentFeed?.videos?.[clipIndex]?.id || '')}
                videoIds={(currentFeed?.videos || []).map((video) => String(video.id))}
                seconds={videoControls.playing ? videoControls.playedSeconds : videoPlayedSeconds}
                selectedClip={selectedDrawingClip ? {
                  id: String(selectedDrawingClip.id), time_start: selectedDrawingClip.time_start, time_end: selectedDrawingClip.time_end,
                  label: `${selectedDrawingClip.topicName || 'Selected clip'}, clip ${(selectedDrawingClip.clipIndex ?? 0) + 1}`,
                } : undefined}
                playbackClip={drawingPlaybackClip ? {
                  id: String(drawingPlaybackClip.id), time_start: drawingPlaybackClip.time_start, time_end: drawingPlaybackClip.time_end,
                  label: `${drawingPlaybackClip.topicName || 'Clip'}, clip ${(drawingPlaybackClip.clipIndex ?? 0) + 1}`,
                } : undefined}
                hasSelectedClips={!!sortedSelectedClips.length}
                showVideoDrawingsWhenPlayingClips={showVideoDrawingsWhenPlayingClips}
                autoStop={!videoControls.seeking}
                jumpToMarker={(comment: COMMENT, automatic) => {
                  const index = currentFeed.videos.findIndex((video) => String(video.id) === comment.drawing?.videoId);
                  if (index < 0) return false;
                  if (comment.clip_id != null) {
                    const rowIndex = videoRows.findIndex((row) => row.clips.some((clip) => String(clip.id) === String(comment.clip_id)));
                    const clipIndex = rowIndex >= 0 ? videoRows[rowIndex].clips.findIndex((clip) => String(clip.id) === String(comment.clip_id)) : -1;
                    const clip = videoRows[rowIndex]?.clips[clipIndex];
                    if (!clip) return false;
                    if (String(clip.id) !== String(markedClip?.id)) drawingAngleOverrideClip.current = String(clip.id);
                    if (!automatic && !sortedSelectedClips.some((selected) => String(selected.id) === String(clip.id))) {
                      dispatch(updateSortedSelectedClips([{ ...clip, rowIndex, clipIndex, topicId: videoRows[rowIndex].id, topicName: videoRows[rowIndex].name }]));
                    }
                    dispatch(imperativeMarkerSetter({ rowIndex, clipIndex, seconds: comment.timestamp }));
                  } else if (!automatic) {
                    dispatch(updateSortedSelectedClips([]));
                  }
                  if (index !== clipIndex) switchVideoClip(clipIndex, index, comment.timestamp);
                  if (pendingClipSwitchRef.current) {
                    pendingClipSwitchRef.current.time = comment.timestamp;
                    pendingClipSwitchRef.current.wasPlaying = false;
                  }
                  const pausedControls = { ...videoControlsRef.current, playedSeconds: comment.timestamp, playing: false };
                  videoControlsRef.current = pausedControls;
                  videoPlayerRef.current?.getInternalPlayer()?.pause?.();
                  seekPlayersTo(comment.timestamp);
                  dispatch(updateVideoPlayedSeconds(comment.timestamp));
                  dispatch(updateVideoControls(pausedControls));
                  return true;
                }}
                pause={() => dispatch(updateVideoControls({ ...videoControls, playing: false }))} />
              <div className={css.shadowPlayControl} onClick={() => playPauseHandler()} />

              <div className={css.playPauseWrapper} data-video-play-overlay>
                {!videoControls.playing && (
                  <PlayCircleOutlineOutlinedIcon
                    className={cx(css.playIcon, !showPauseIcon ? css.hide : '')}
                    style={{ display: 'block !important' }}
                    onClick={() => playPauseHandler()}
                  />
                )}
              </div>
              {!fullScreen.active && (
                <div className={cx(css.notes, !showNotes ? css.zeroHeight : '')}>
                  <NotesForVideo className={css.notesInner} switchShowNotes={switchNotesCallBack} showNotes={showNotes} />
                </div>
              )}
              <div className={cx(css.controls, (showControls || fullScreen.active) && css.showControls)}>
                {videoPlayerRef.current && (
                  <VideoControls
                    controlRef={controlRef}
                    played={videoControls.played}
                    onSeek={seekHandler}
                    onSeekMouseUp={seekMouseUpHandler}
                    onMouseSeekDown={onSeekMouseDownHandler}
                    currentTime={formatCurrentTime}
                    onPlayPause={playPauseHandler}
                    playing={videoControls.playing}
                    fullScreenHandler={switchFullScreen}
                    activeFullScreen={fullScreen.active}
                    changePlayBackRate={changePlayBackRate}
                    videoVolumeChange={videoVolumeChange}
                    volume={videoControls.volume}
                    playBackRate={videoControls.playbackRate}
                    videoData={videoData}
                  />
                )}
              </div>
              </div>
            </FullScreen>
          )}
          <FullPageLoader loading={loading} />
        </div>
      )}
    </>
  );
};

export default VideoPlayer;
